import axios from "axios";
import { toast } from "sonner";

const altingrossapi = axios.create({
  timeout: 10000,
  baseURL: process.env.REACT_APP_ALTINGROSS_API_URL || "http://localhost:5297/api/v1",
});

altingrossapi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access-token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

altingrossapi.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { response } = error;
    const status = response?.status;

    let data = response?.data;
    if (data instanceof Blob) {
      try { data = JSON.parse(await data.text()); } catch { data = null; }
    }

    // ExceptionMessage varsa error.message'a taşı — slice'lar rejectWithValue'da kullanır
    const apiMessage = data?.ExceptionMessage ?? data?.message ?? null;
    if (apiMessage) error.message = apiMessage;

    switch (status) {
      case 400:
        toast.error(apiMessage ?? "İstek geçersiz. (400)");
        break;
      case 401:
        // Token geçersiz / süresi dolmuş → AuthContext'e haber ver ve login'e yönlendir
        window.dispatchEvent(new CustomEvent("auth-failed"));
        window.location.href = "/login";
        break;
      case 403:
        toast.error("Bu işlem için yetkiniz yok. (403)");
        break;
      case 404:
        // 404'ü sessizce geç — bazı GET kontrolleri bilerek 404 kullanır
        break;
      case 422:
        toast.error(data?.Message ?? "Gönderilen veri formatı geçersiz. (422)");
        break;
      case 500:
        toast.error(apiMessage ?? "Beklenmedik bir sunucu hatası oluştu. (500)");
        break;
      default:
        if (status) toast.error(`Bir hata oluştu. (${status})`);
        break;
    }

    return Promise.reject(error);
  }
);

export default altingrossapi;

const BASE = 'NewsLetterSubscribers';

export const newsLetterSubscriberService = {
  getById: (id) =>
    altingrossapi.get(`${BASE}/${id}`),

  getList: (page, size, filter = null) => {
    const params = { page, size };
    if (filter?.field && filter?.value && filter?.operator !== undefined) {
      params.field    = filter.field;
      params.value    = filter.value;
      params.operator = filter.operator;
    }
    return altingrossapi.get(BASE, { params });
  },

  exportExcel: (fileName, sheetName) =>
    altingrossapi.get(`${BASE}/export`, {
      params: { fileName, sheetName },
      responseType: 'blob',
    }),

  deleteById: (id) =>
    altingrossapi.delete(`${BASE}/admin/${id}`),
};

const CAMPAIGN_BASE = 'Campaigns';

export const campaignService = {
  getById: (id) =>
    altingrossapi.get(`${CAMPAIGN_BASE}/${id}`),

  getList: (page, size, filter = null) => {
    const params = { page, size };
    if (filter?.field && filter?.value !== undefined && filter?.operator !== undefined) {
      params.field    = filter.field;
      params.value    = filter.value;
      params.operator = filter.operator;
    }
    return altingrossapi.get(CAMPAIGN_BASE, { params });
  },

  update: (id, body) =>
    altingrossapi.put(`${CAMPAIGN_BASE}/${id}`, body),

  deleteById: (id) =>
    altingrossapi.delete(`${CAMPAIGN_BASE}/${id}`),

  createTemplateWithAi: (prompt) =>
    altingrossapi.post(`${CAMPAIGN_BASE}/create-template-with-ai`, { prompt }),

  create: (body) =>
    altingrossapi.post(CAMPAIGN_BASE, body),

  sendMail: (id) =>
    altingrossapi.post(`${CAMPAIGN_BASE}/${id}/send-mail`),
};

// ─── Heros Service ─────────────────────────────────────────────────────────
const HERO_BASE = 'Heros';

const buildHeroFormData = (data, includeId = false) => {
  const form = new FormData();
  if (includeId && data.id)  form.append('id',          data.id);
  if (data.title)            form.append('title',       data.title);
  if (data.subtitle)         form.append('subtitle',    data.subtitle);
  if (data.description)      form.append('description', data.description);
  if (data.image)            form.append('image',       data.image);
  if (data.buttonText)       form.append('buttonText',  data.buttonText);
  if (data.buttonUrl)        form.append('buttonUrl',   data.buttonUrl);
  form.append('Order',    String(data.order    ?? 0));
  form.append('IsActive', String(data.isActive ?? true));
  return form;
};

export const heroService = {
  getById: (id) =>
    altingrossapi.get(`${HERO_BASE}/${id}`),

  getList: (page, size, filter = null) => {
    const params = { page, size };
    if (filter?.field && filter?.value !== undefined && filter?.operator !== undefined) {
      params.field    = filter.field;
      params.value    = filter.value;
      params.operator = filter.operator;
    }
    return altingrossapi.get(HERO_BASE, { params });
  },

  create: (data) =>
    altingrossapi.post(HERO_BASE, buildHeroFormData(data, false)),

  update: (id, data) =>
    altingrossapi.put(`${HERO_BASE}/${id}`, buildHeroFormData({ ...data, id }, true)),

  deleteById: (id) =>
    altingrossapi.delete(`${HERO_BASE}/${id}`),
};

// ─── About Service ─────────────────────────────────────────────────────────
const ABOUT_BASE = 'Abouts';

const buildAboutFormData = (data, includeId = false) => {
  const form = new FormData();
  if (includeId && data.id)  form.append('id',             data.id);
  if (data.title)            form.append('title',          data.title);
  if (data.content)          form.append('content',        data.content);
  if (data.experienceText)   form.append('experienceText', data.experienceText);
  if (data.image instanceof File) form.append('image',     data.image);
  (data.features ?? []).forEach((f, i) => {
    form.append(`features[${i}].icon`,        f.icon        ?? '');
    form.append(`features[${i}].title`,       f.title       ?? '');
    form.append(`features[${i}].description`, f.description ?? '');
  });
  return form;
};

export const aboutService = {
  getSingle: () =>
    altingrossapi.get(`${ABOUT_BASE}/single`),

  create: (data) =>
    altingrossapi.post(ABOUT_BASE, buildAboutFormData(data, false)),

  update: (id, data) =>
    altingrossapi.put(`${ABOUT_BASE}/${id}`, buildAboutFormData({ ...data, id }, true)),
};

// ─── Category Service ───────────────────────────────────────────────────────
const CAT_BASE = 'Categories';

export const categoryService = {
  getById: (id) =>
    altingrossapi.get(`${CAT_BASE}/${id}`),

  getList: ({ page = 1, size = 20, field, value, operator } = {}) => {
    const params = { page, size };
    if (field && value && operator) Object.assign(params, { field, value, operator });
    return altingrossapi.get(CAT_BASE, { params });
  },

  create: (data) =>
    altingrossapi.post(CAT_BASE, data),

  update: (id, data) =>
    altingrossapi.put(`${CAT_BASE}/${id}`, data),

  deleteById: (id) =>
    altingrossapi.delete(`${CAT_BASE}/${id}`),
};

// ─── Policy Service ─────────────────────────────────────────────────────────
const POLICY_BASE = 'Policies';

export const policyService = {
  getById: (id) =>
    altingrossapi.get(`${POLICY_BASE}/${id}`),

  getList: ({ page = 1, size = 20, field, value, operator } = {}) => {
    const params = { page, size };
    if (field && value && operator) Object.assign(params, { field, value, operator });
    return altingrossapi.get(POLICY_BASE, { params });
  },

  create: (data) =>
    altingrossapi.post(POLICY_BASE, data),

  update: (id, data) =>
    altingrossapi.put(`${POLICY_BASE}/${id}`, data),

  deleteById: (id) =>
    altingrossapi.delete(`${POLICY_BASE}/${id}`),
};

// ─── Message Service ─────────────────────────────────────────────────────────
const MSG_BASE = 'Messages';

export const messageService = {
  getById: (id) =>
    altingrossapi.get(`${MSG_BASE}/${id}`),

  getList: ({ page = 1, size = 20, field, value, operator } = {}) => {
    const params = { page, size };
    if (field && value && operator) Object.assign(params, { field, value, operator });
    return altingrossapi.get(MSG_BASE, { params });
  },

  answer: (id, responseNote) =>
    altingrossapi.put(`${MSG_BASE}/${id}/answer`, { responseNote }),

  confirm: (id) =>
    altingrossapi.put(`${MSG_BASE}/${id}/confirm`, {}),

  deleteById: (id) =>
    altingrossapi.delete(`${MSG_BASE}/${id}`),
};

// ─── Branch Service ──────────────────────────────────────────────────────────
const BRANCH_BASE = 'Branches';

export const branchService = {
  getById: (id) =>
    altingrossapi.get(`${BRANCH_BASE}/${id}`),

  getList: ({ page = 1, size = 20, field, value, operator } = {}) => {
    const params = { page, size };
    if (field && value && operator) Object.assign(params, { field, value, operator });
    return altingrossapi.get(BRANCH_BASE, { params });
  },

  create: (data) =>
    altingrossapi.post(BRANCH_BASE, data),

  update: (id, data) =>
    altingrossapi.put(`${BRANCH_BASE}/${id}`, { id, ...data }),

  deleteById: (id) =>
    altingrossapi.delete(`${BRANCH_BASE}/${id}`),
};

// ─── SEO Service ─────────────────────────────────────────────────────────────
const SEO_BASE = 'SeoSettings';

const buildSeoFormData = (data, includeId = false) => {
  const fd = new FormData();
  if (includeId && data.id) fd.append('id', data.id);

  const str  = (key, val) => { if (val !== undefined && val !== null) fd.append(key, String(val)); };
  const file = (key, val) => { if (val instanceof File) fd.append(key, val); };

  str('siteTitle',               data.siteTitle);
  str('metaDescription',         data.metaDescription);
  str('keywords',                data.keywords);
  str('robots',                  data.robots);
  str('canonicalUrl',            data.canonicalUrl);
  str('noIndex',                 data.noIndex);
  str('noFollow',                data.noFollow);
  str('ogSiteName',              data.ogSiteName);
  str('ogTitle',                 data.ogTitle);
  file('ogImage',                data.ogImage);
  str('ogDescription',           data.ogDescription);
  str('ogUrl',                   data.ogUrl);
  str('ogType',                  data.ogType);
  str('twitterCard',             data.twitterCard);
  str('twitterSite',             data.twitterSite);
  str('twitterCreator',          data.twitterCreator);
  str('twitterTitle',            data.twitterTitle);
  str('twitterDescription',      data.twitterDescription);
  str('googleAnalyticsId',       data.googleAnalyticsId);
  str('googleSearchConsoleCode', data.googleSearchConsoleCode);
  str('facebookPixelId',         data.facebookPixelId);
  str('yandexMetricalId',        data.yandexMetricaId);   // API typo
  str('hatjorId',                data.hotjarId);           // API typo
  str('clarityId',               data.clarityId);
  str('facebookUrl',             data.facebookUrl);
  str('InstagramUrl',            data.instagramUrl);       // API capital I
  str('twitterUrl',              data.twitterUrl);
  str('linkedinUrl',             data.linkedinUrl);
  str('youtubeUrl',              data.youtubeUrl);
  str('whatsappNumber',          data.whatsappNumber);
  str('PinterestUrl',            data.pinterestUrl);       // API capital P
  str('tiktokUrl',               data.tiktokUrl);
  str('robotsTxt',               data.robotsTxt);
  str('schemaOrg',               data.schemaOrg);
  str('customHeadScripts',       data.customHeadScripts);
  str('customBodyStartScripts',  data.customBodyStartScripts);
  str('customBodyEndScripts',    data.customBodyEndScripts);

  return fd;
};

export const seoService = {
  getSingle: () =>
    altingrossapi.get(`${SEO_BASE}/single`),

  create: (data) =>
    altingrossapi.post(SEO_BASE, buildSeoFormData(data, false), {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  update: (id, data) =>
    altingrossapi.put(`${SEO_BASE}/${id}`, buildSeoFormData(data, true), {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

// ─── Guest Service ───────────────────────────────────────────────────────────
const GUEST_BASE = 'Guests';

export const guestService = {
  getById: (id) =>
    altingrossapi.get(`${GUEST_BASE}/${id}`),

  getList: ({ page = 1, size = 20, field, value, operator } = {}) => {
    const params = { page, size };
    if (field && value && operator) Object.assign(params, { field, value, operator });
    return altingrossapi.get(GUEST_BASE, { params });
  },

  deleteById: (id) =>
    altingrossapi.delete(`${GUEST_BASE}/${id}`),
};
